import { useAuthentication } from '@/composables/useAuthentication';
import { isValidChartListListNum } from '@/composables/isValidChartListListNum';
import { JsonLayerChart, isJsonLayerChart } from '@/interfaces/json-layer-responses/jsonLayerChart';
import { JsonLayerResponse, isJsonLayerResponse } from '@/interfaces/jsonLayerResponse';

interface FetchChartsResponse extends JsonLayerResponse {
  favorites: any[];
}

function isFetchChartsResponse(jsonLayerResponse: JsonLayerResponse): jsonLayerResponse is FetchChartsResponse {
  return (
    'favorites' in jsonLayerResponse &&
    Array.isArray(jsonLayerResponse.favorites)
  );
}

export async function fetchCharts(chartListListNum: string): Promise<JsonLayerChart[]> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isValidChartListListNum(chartListListNum)) {
    throw new Error(`Unable to fetch Charts for chartListListNum - chartListListNum argument is not a valid ChartList listNum: ${typeof chartListListNum} ${chartListListNum}.`);
  }

  if (!isLoggedIn.value) {
    throw new Error(`Unable to fetch Charts for chartListListNum: ${chartListListNum} - user is not logged in.`);
  }

  const chartCodeCopy = chartCode.value;
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'get-favorites',
        chartCode: chartCodeCopy,
        listnum: chartListListNum
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info(`Fetch Charts jsonLayerResponse for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum}.`, jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to fetch Charts for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum}.`, e);

    throw new Error(`Failed to fetch Charts for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to fetch charts for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to fetch charts for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }

  if (!isFetchChartsResponse(jsonLayerResponse)) {
    throw new Error(`Failed to fetch charts for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum} - jsonLayerResponse.success is true but jsonLayerResponse does not match FetchChartsResponse interface - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }

  const jsonLayerCharts = jsonLayerResponse.favorites.filter(isJsonLayerChart);
  const numInvalidJsonLayerCharts = jsonLayerResponse.favorites.length - jsonLayerCharts.length;

  if (numInvalidJsonLayerCharts > 0) {
    window.sc3WorkbenchLogger.warn(`json-layer get-favorites command returned ${numInvalidJsonLayerCharts} invalid favorites (which did not match the JsonLayerChart interface) for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum}.`);
  }

  return jsonLayerCharts;
}
